import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ExpandMore} from '@mui/icons-material';
import { Select, MenuItem, Tooltip } from '@mui/material';
import { fontSize } from '../../..';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import '../../ChartAxes/Card.css';
import LockIcon from '@mui/icons-material/Lock';
import { useDispatch } from 'react-redux';
import { updateSourceAggregation } from '../../../redux/Calculations/CalculationsActions';
import { useViewport } from '@xyflow/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { WarningAmber } from '@mui/icons-material';
import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';
import {
  ICalculationFields,
  IFlows,
} from '../../../redux/Calculations/CurrentCalculationSessionInterface';

interface SourceListCardProps {
  cardType: 'field' | 'flow';
  displayName: string;
  aggregationMethod: string;
  index: number;
  value: any;
  isAggregationActive: boolean;
  propKey: string;
  calculationFlowUID: string;
  isFlowUsedInOtherFlowsAsSource: {
    isUsed: boolean;
    allFlowsWithFlowAsSource: string[];
  };
  missingFieldsLength: number;
  flows: IFlows;
  fields?: ICalculationFields;

  moveCard: (dragIndex: number, hoverIndex: number) => void;
  handleSourceCardCrossButton: (sourceId: string, sourceIndex: number) => void;
  updateAggregation?: (
    propKey: string,
    calculationFlowUID: string,
    step: number,
    sourceIndex: number,
    newAggregation: string,
  ) => void;
  updateParameterIndex: (index: number, dragIndex: number, type: any) => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
  parameterId?: string;
}

export const sourceListDownArrowOptions = [
  { displayName: 'sum', value: 'sum' },
  { displayName: 'avg', value: 'avg' },
  { displayName: 'min', value: 'min' },
  { displayName: 'max', value: 'max' },
  { displayName: 'count', value: 'count' },
  { displayName: 'count Non Null', value: 'countnn' },
  { displayName: 'count Null', value: 'countn' },
  { displayName: 'count Unique', value: 'countu' },
];

const SourceListCard: React.FC<SourceListCardProps> = ({
  displayName,
  aggregationMethod,
  index,
  isAggregationActive,
  propKey,
  calculationFlowUID,
  moveCard,
  value,
  isFlowUsedInOtherFlowsAsSource,
  handleSourceCardCrossButton,
  missingFieldsLength,
  flows,
  fields,
  cardType,
  updateParameterIndex,
}) => {
  const { tablesForSelectedDataSets, chartProperties } = useSelector((state: RootState) => {
    return {
      tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
      chartProperties: state.chartProperties,
    };
  });
  const dispatch = useDispatch();
 const { zoom } = useViewport();
  const [sourceCardHoverActive, setSourceCardHoverActive] = useState(false);
  const [isDownArrowActive, setIsDownArrowActive] = useState(false);
  const [selectedDisplayName, setSelectedDisplayName] = React.useState('sum');
  const [alert, setAlert] = useState<any>(null);

  const ref = useRef<HTMLDivElement>(null);

  const selectedDs = chartProperties.properties[propKey].selectedDs;

  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  // introduce step
  const handleSourceAggregationUpdate = (sourceIndex: number, newAggregation: string) =>
    dispatch(updateSourceAggregation(propKey, calculationFlowUID, 0, sourceIndex, newAggregation));

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null }>({
    accept: ['source-card', 'parameter-card'],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      updateParameterIndex(hoverIndex, dragIndex, item);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag<DragItem, unknown, { isDragging: boolean }>({
    type: 'source-card',
    item: (): DragItem => {
      return { id: value, index, type: 'source-card' };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const getAggregationStatus = (flows: any, flowId: string) => {
    const aggregationResult = flows[flowId][0].isAggregation;

    return aggregationResult;
  };

  const matchedFlow = flows[calculationFlowUID];

  const sourceValue = matchedFlow[0]?.sourceType?.includes('flow')
    ? matchedFlow[0].source[index]
    : null;

  const checkAggregationStatus = (flowId: string) => {
    const result = getAggregationStatus(flows, flowId);

    return result;
  };

  useEffect(() => {
    if (!fields || !fields?.[value]) return;
    (async () => {
      try {
        const res = await checkIfMissingColumn(
          fields[value],
          selectedDs,
          tablesForSelectedDataSets[selectedDs.id],
        );
        setIsShowWarning(res.isMissing);
      } catch (error) {
        console.error('Error checking missing column:', error);
        setIsShowWarning(true);
      }
    })();
  }, [fields, missingFieldsLength, value]);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setSourceCardHoverActive(true)}
      onMouseLeave={() => setSourceCardHoverActive(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: isDragging ? 0 : 1,
        cursor: 'move',
        position: 'relative',
        border: isShowWarning ? '1px solid #850000ff' : '1px solid rgb(211, 211, 211)',
        borderRadius: '2px',
        // margin: '2px 4px',
        fontSize: fontSize.medium,
        padding: '0px 8px',
        height: isAggregationActive ? '24px' : 'auto',
        minHeight: '24px',
        overflow: 'hidden',
      }}
      data-handler-id={handlerId}
    >
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          userSelect: 'none',
          maxWidth: isAggregationActive ? '60%' : '90%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {!isFlowUsedInOtherFlowsAsSource.isUsed && (
          <button
            type="button"
            className="buttonCommon columnClose"
            title={isFlowUsedInOtherFlowsAsSource.isUsed ? 'no' : 'Remove field'}
            onClick={() => {
              if (!flows) return;
              if (!(Object.keys(flows).length > 0)) return;

              if (isFlowUsedInOtherFlowsAsSource.isUsed) {
                setAlert({
                  severity: 'warning',
                  message: 'Can not delete source of a flow which is used in another flow',
                });
                return;
              }

              handleSourceCardCrossButton(value, index);
            }}
            style={{ width: '13px', height: '13px', flexShrink: 0 }}
          >
            {
              <CloseRoundedIcon
                style={{
                  fontSize: '13px',
                  display: sourceCardHoverActive ? 'block' : 'none',
                }}
              />
            }
          </button>
        )}

        {isFlowUsedInOtherFlowsAsSource.isUsed && (
          <Tooltip
            title="Can not edit source of a flow which is used in another flow"
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <LockIcon
              style={{
                fontSize: '13px',
                color: '#2BB9BB',
              }}
            />
          </Tooltip>
        )}

        <Tooltip
          title={displayName}
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <span
            style={{
              margin: 0,
              userSelect: 'none',
              alignContent: 'center',
              overflow: isAggregationActive ? 'hidden' : 'visible',
              textOverflow: isAggregationActive ? 'ellipsis' : 'clip',
              whiteSpace: isAggregationActive ? 'nowrap' : 'normal',
              wordBreak: isAggregationActive ? 'normal' : 'break-word',
            }}
          >
            {displayName}
          </span>
        </Tooltip>
      </div>
      {cardType === 'field' && (
        <div style={{ display: 'flex',justifyContent :  "end", alignItems: 'center', marginLeft: 'auto'}}>
          {isAggregationActive && (
            <span style={{ userSelect: 'none' }}>
              {isAggregationActive ? aggregationMethod : ''}
            </span>
          )}
          {isAggregationActive && !isFlowUsedInOtherFlowsAsSource.isUsed && (
            <Select
              size="small"
              sx={{
                backgroundColor: 'white',
                cursor: 'auto',
                border: 'none',
                outline: 'none',
                '.MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                boxShadow: 'none',
                '& .MuiSelect-select': {
                  padding: '0px !important',
                  paddingRight: '24px !important', 
                  display: 'flex',
                  alignItems: 'center',
                },

                '& .MuiSelect-icon': {
                  right: '0px', 
                  margin: 0,
                },
              }}
              IconComponent={ExpandMore}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      transform: `scale(${zoom}) !important`
                    }
                  }
                },
              }}
            >
              {sourceListDownArrowOptions.map((item, id) => {
                return (
                  <MenuItem
                    sx={{
                      listStyle: 'none',
                      cursor: 'pointer',
                      color: 'black',
                      backgroundColor:
                        selectedDisplayName === item.displayName
                          ? 'rgba(43, 185, 187, 0.5) !important'
                          : 'white !important',
                      '&:hover': {
                        backgroundColor: 'rgba(43, 185, 187, 0.3) !important',
                      },
                    }}
                    key={id}
                    title="Window Function"
                    onClick={() => {
                      setIsDownArrowActive(false);
                      setSelectedDisplayName(item.displayName);
                      handleSourceAggregationUpdate(index, item.value);
                    }}
                  >
                    {item.displayName}
                  </MenuItem>
                );
              })}
            </Select>
          )}
          {isShowWarning && (
            <Tooltip
              title="This column is missing in the dataset"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <WarningAmber
                style={{
                  color: '#850000ff',
                }}
                fontSize="small"
              />
            </Tooltip>
          )}
        </div>
      )}
      {cardType === 'flow' && (
        <div style={{ alignItems: 'center', marginLeft: 'auto', flexShrink: 0,marginRight : "22px" }}>
          {isAggregationActive && (
            <span style={{ userSelect: 'none', marginLeft: '10px' }} className="columnPrefix">
              {checkAggregationStatus(sourceValue) ? 'Agg' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SourceListCard;
